import { Block, Card, CardContent } from 'framework7-react';
import type { DeliveryReadiness, DeliveryReadinessProblem } from '@restaurant/api-client';
import { useT } from '@/i18n';

/**
 * Amber card listing the delivery-setup mistakes that would break checkout for a
 * real guest.
 *
 * Each row states the mistake AND what the customer actually experiences,
 * because these failures are silent and most of them blame the guest — an
 * unpinned restaurant tells every customer "we don't deliver to your address".
 * Without the consequence line an owner has no reason to believe the warning
 * matters.
 */
export function DeliverySetupWarnings({ readiness }: { readiness?: DeliveryReadiness }) {
  const w = useT().orderSetup.page.deliveryWarnings;

  // Nothing to say when delivery is off or the setup is sound. A silent, healthy
  // screen is what makes the amber card mean something when it does appear.
  if (!readiness?.deliveryEnabled || readiness.problems.length === 0) return null;

  const copy = (problem: DeliveryReadinessProblem) => {
    switch (problem.code) {
      case 'LOCATION_NOT_PINNED':
        return { title: w.locationNotPinned, effect: w.locationNotPinnedEffect };
      case 'NO_ACTIVE_ZONES':
        return { title: w.noActiveZones, effect: w.noActiveZonesEffect };
      case 'ZONE_MISSING_DELIVERY_TIME':
        return {
          title: w.zoneMissingTime(problem.zoneLabel ?? ''),
          effect: w.zoneMissingTimeEffect,
        };
      case 'NO_PAYMENT_METHOD':
        return { title: w.noPaymentMethod, effect: w.noPaymentMethodEffect };
    }
  };

  return (
    <Block style={{ marginTop: 16, marginBottom: 0 }}>
      <Card
        outline
        style={{
          margin: 0,
          borderColor: 'var(--f7-color-orange)',
          background: 'rgba(255, 149, 0, 0.08)',
        }}
      >
        <CardContent>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontWeight: 600,
              color: 'var(--f7-color-orange)',
            }}
          >
            <i className="f7-icons" style={{ fontSize: 20 }}>
              exclamationmark_triangle_fill
            </i>
            <span>{w.title}</span>
          </div>
          <div style={{ marginTop: 4, fontSize: 14, opacity: 0.75 }}>{w.subtitle}</div>

          <ul style={{ margin: '12px 0 0', padding: 0, listStyle: 'none' }}>
            {readiness.problems.map((problem, i) => {
              const { title, effect } = copy(problem);
              return (
                <li
                  key={`${problem.code}-${problem.zoneLabel ?? i}`}
                  style={{
                    paddingTop: 10,
                    marginTop: 10,
                    borderTop: i === 0 ? 'none' : '1px solid rgba(255, 149, 0, 0.25)',
                  }}
                >
                  <div style={{ fontWeight: 500 }}>{title}</div>
                  {/* The consequence, in the guest's words — this is the part
                      that makes an owner act. */}
                  <div style={{ marginTop: 2, fontSize: 13, opacity: 0.7 }}>{effect}</div>
                </li>
              );
            })}
          </ul>
        </CardContent>
      </Card>
    </Block>
  );
}
